import React, { createContext, useContext, useState, useEffect } from 'react'

interface User {
  id: string
  email: string
  nombre: string
  rol: string
  foto_url: string | null
  preferencias: {
    texto_grande?: boolean
  }
}

// Apply texto_grande preference to DOM
function applyTextoGrande(enabled: boolean) {
  document.documentElement.setAttribute('data-texto-grande', String(enabled))
  // Also store in localStorage for instant apply on page load (before user data loads)
  localStorage.setItem('texto_grande', String(enabled))
}

// Initialize from localStorage immediately (prevents flash)
const storedTextoGrande = localStorage.getItem('texto_grande')
if (storedTextoGrande === 'true') {
  document.documentElement.setAttribute('data-texto-grande', 'true')
}

interface UserContextType {
  user: User | null
  loading: boolean
  error: string | null
  fetchUser: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
  logout: () => void
  setTextoGrande: (enabled: boolean) => Promise<boolean>
  textoGrande: boolean
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [textoGrande, setTextoGrandeState] = useState(() => localStorage.getItem('texto_grande') === 'true')

  // Best-effort local JWT expiry check so we don't fire a doomed /auth/me request
  // when the token in localStorage has clearly expired (most common cause of the
  // "401 Unauthorized" noise on the public landing page).
  const isJwtExpired = (token: string): boolean => {
    try {
      const part = token.split('.')[1]
      if (!part) return false
      const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')))
      if (typeof payload.exp === 'number') {
        return Date.now() / 1000 >= payload.exp
      }
    } catch {
      // Unparseable -> let the server decide
    }
    return false
  }

  const fetchUser = async () => {
    try {
      setError(null)
      const token = localStorage.getItem('auth_token')
      if (!token || isJwtExpired(token)) {
        if (token) localStorage.removeItem('auth_token')
        setUser(null)
        setLoading(false)
        return
      }

      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      // Stale or revoked token — clean up silently so it doesn't fire on every reload
      if (response.status === 401) {
        localStorage.removeItem('auth_token')
        setUser(null)
        return
      }

      const result = await response.json()
      if (result.success) {
        setUser(result.data)
      } else {
        setError(result.error)
        setUser(null)
      }
    } catch (err) {
      console.error('Error fetching user:', err)
      setError('Error al cargar los datos del usuario')
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData })
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('texto_grande')
    applyTextoGrande(false)
    setTextoGrandeState(false)
    setUser(null)
  }

  // Set texto_grande with API call and DOM update
  const setTextoGrande = async (enabled: boolean): Promise<boolean> => {
    // Apply immediately to DOM for instant feedback
    applyTextoGrande(enabled)
    setTextoGrandeState(enabled)

    // If not logged in, just apply locally
    const token = localStorage.getItem('auth_token')
    if (!token) {
      return true
    }

    // Save to server
    try {
      const response = await fetch('/api/auth/preferences', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ texto_grande: enabled })
      })

      const result = await response.json()
      if (result.success && user) {
        setUser({ ...user, preferencias: result.data.preferencias })
        return true
      }
      return false
    } catch (err) {
      console.error('Error saving texto_grande preference:', err)
      return false
    }
  }

  // Fetch user on mount
  useEffect(() => {
    fetchUser()
  }, [])

  // Sync texto_grande from user preferences when user loads
  useEffect(() => {
    if (user?.preferencias?.texto_grande !== undefined) {
      const userPref = user.preferencias.texto_grande
      applyTextoGrande(userPref)
      setTextoGrandeState(userPref)
    }
  }, [user?.preferencias?.texto_grande])

  const value: UserContextType = {
    user,
    loading,
    error,
    fetchUser,
    updateUser,
    logout,
    setTextoGrande,
    textoGrande
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}